/** @jsxImportSource @emotion/react */
import {useCallback, useMemo, useRef, useState} from 'react';
import {useEffect} from 'react';

import {css} from '@emotion/react';
import {FmlxDropdown} from 'fmlx-common-ui';
import moment from 'moment';
import {useSelector} from 'react-redux';

import dateIconDisable from '@/assets/icons/date-disable.svg';
import dateIconEnable from '@/assets/icons/date-enable.svg';
import DateTimePicker from '@/components/dateTimePicker/DateTimePicker';
import ToggleButtonCustom from '@/components/togglebutton/src/ToggleButtonCustom';
import {ButtonSize} from '@/constants/appStyle';
import {VERSION_2} from '@/constants/constant';
import {HISTORY_SHOW_TYPE, HISTORY_SHOW_TYPE_DATE_OPTION} from '@/constants/enums';
import {useCycleHistoryFilterContext, useCycleHistoryProfileModeContext} from '@/contexts/CycleHistoryContext';

import './CycleHistoryPage.scss';

const MENU_ITEM_DROPDOWN_HEIGHT = 40;

const sortingOptions = [
  {value: HISTORY_SHOW_TYPE_DATE_OPTION.NEWER, label: 'Newest'},
  {value: HISTORY_SHOW_TYPE_DATE_OPTION.OLDER, label: 'Oldest'},
  {value: '50ML', label: '50 ML'},
  {value: '15ML', label: '15 ML'},
];

const sortingOptionsManual = [
  {value: HISTORY_SHOW_TYPE_DATE_OPTION.NEWER, label: 'Newest'},
  {value: HISTORY_SHOW_TYPE_DATE_OPTION.OLDER, label: 'Oldest'},
];

export function CycleHistoryBottomMenu() {
  const [isDisplayBeginDateSelection, setIsDisplayBeginDateSelection] = useState(false);
  const [isDisplayEndDateSelection, setIsDisplayEndDateSelection] = useState(false);
  const [isSortToOldest, setSortToOldest] = useState(true);
  const {
    showType,
    showOrder,
    searchFilterBegin,
    searchFilterEnd,
    SetShowType,
    SetShowOrder,
    SetSearchFilterBegin,
    SetSearchFilterEnd,
  } = useCycleHistoryFilterContext();
  const {profileMode} = useCycleHistoryProfileModeContext();
  const [sortingItems, setSortingItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(1);

  const sortRef = useRef(null);

  const deviceVersion = useSelector((store) => store.setupStore.deviceVersion);

  useEffect(() => {
    let sortingArray = [];
    if (profileMode === 'Manual Profil = sortingOptions;' || deviceVersion === VERSION_2)
      sortingArray = sortingOptionsManual;
    else sortingArray = sortingOptions;

    const values = sortingArray.map((val, index) => ({
      id: index + 1,
      text: val.label,
      group: val.value,
      value: val.value,
    }));
    setSortingItems(values);

    const item = values.find((value) => value.value === showOrder);
    if (item) setSelectedItem(item.id);
  }, [SetShowType, deviceVersion, profileMode, showOrder]);

  const iconShowTypeDate = useMemo(
    () => (showType === HISTORY_SHOW_TYPE.DATE ? dateIconEnable : dateIconDisable),
    [showType],
  );

  const disableDateAction = useMemo(() => showType === HISTORY_SHOW_TYPE.ALL, [showType]);

  const showTypeButtonProps = useMemo(
    () => [
      {
        label: HISTORY_SHOW_TYPE.ALL,
        value: HISTORY_SHOW_TYPE.ALL,
        customButtonSize: true,
      },
      {
        label: HISTORY_SHOW_TYPE.DATE,
        value: HISTORY_SHOW_TYPE.DATE,
        icon: iconShowTypeDate,
        customButtonSize: true,
      },
    ],
    [iconShowTypeDate],
  );

  /**
   * @param {Event} evt
   */
  const OnShowTypeChangeActionHandler = useCallback(
    (evt) => {
      const {id} = evt.currentTarget;
      if (showType === id) return;
      SetShowType(id);
    },
    [showType],
  );

  const OnShowSortChangeActionHandler = (evt, sortingItems) => {
    if (showOrder === sortingItems[evt - 1].value) return;
    SetShowOrder(sortingItems[evt - 1].value);
  };
  /**
   * @param {Event} evt
   */
  const OnDateTypeOptionChangeActionHandler = (evt) => {
    const {id} = evt.currentTarget;
    switch (id) {
      case HISTORY_SHOW_TYPE_DATE_OPTION.SORT:
        setSortToOldest(!isSortToOldest);
        break;
      case HISTORY_SHOW_TYPE_DATE_OPTION.SEARCH_FILTER_BEGIN:
        setIsDisplayBeginDateSelection(true);
        break;
      case HISTORY_SHOW_TYPE_DATE_OPTION.SEARCH_FILTER_END:
        setIsDisplayEndDateSelection(true);
        break;

      default:
        break;
    }
  };

  /**
   *
   * @param {String} id
   * @param {Date} selectedDate
   */
  const OnDateSelectionChangeActionHandler = (id, selectedDate) => {
    switch (id) {
      case HISTORY_SHOW_TYPE_DATE_OPTION.SEARCH_FILTER_BEGIN:
        setIsDisplayBeginDateSelection(false);
        selectedDate.setHours(0, 0, 0);
        SetSearchFilterBegin(selectedDate);
        console.log('test begin: ', selectedDate);
        if (moment(selectedDate).isAfter(searchFilterEnd)) {
          setIsDisplayEndDateSelection(false);
          const newEndDate = new Date(selectedDate.getTime());
          newEndDate.setHours(23, 59, 59);
          SetSearchFilterEnd(newEndDate);
        }
        SetShowOrder(HISTORY_SHOW_TYPE_DATE_OPTION.NEWEST);
        break;
      case HISTORY_SHOW_TYPE_DATE_OPTION.SEARCH_FILTER_END:
        setIsDisplayEndDateSelection(false);
        selectedDate.setHours(23, 59, 59);
        SetSearchFilterEnd(selectedDate);
        console.log('test end: ', selectedDate);
        if (moment(selectedDate).isBefore(searchFilterBegin)) {
          setIsDisplayBeginDateSelection(false);
          const newBeginDate = new Date(selectedDate.getTime());
          newBeginDate.setHours(0, 0, 0);
          SetSearchFilterBegin(newBeginDate);
        }
        SetShowOrder(HISTORY_SHOW_TYPE_DATE_OPTION.NEWEST);
        break;
      default:
        break;
    }
  };

  const limitCalendarDateRange = (current) =>
    current.isAfter(moment().subtract(60, 'day')) && current.isBefore(moment());

  const emptyContent = undefined;

  const dash = css({
    width: '16px',
    height: '0px',
    border: '2px solid #FEFEFE',
  });

  const CycleHistoryBottomMenuSearchFilterStyle = css({
    height: ButtonSize.defaultButtonHeight,
    width: '180px',
    color: '#333333',
    textAlign: 'center',
    'border-radius': '8px',
    border: 'none',
    backgroundColor: disableDateAction && '#A7A7A7',
  });
  const CycleHistoryBottomMenuShowTypeStyle = css({
    width: `${90 * 2}px`,
    marginLeft: '10px',
  });

  const SortbyText = css({
    /* 'SORT' 'BY' */

    width: '100px',
    height: '48px',
    marginLeft: '16px',

    /* 'Product'/'Medium'/'text-xl' */

    fontFamily: 'Roboto',
    fontStyle: 'normal',
    fontWeight: '500',
    fontSize: '24px',
    lineHeight: '120%',
    /* 'or' '29px' */

    display: 'flex',
    alignItems: 'center',
    textAlign: 'center',

    /* 'Neutral' 'color'/'Neutral' '13' */

    color: '#FFFFFF',
  });

  return (
    <>
      <div
        css={CycleHistoryBottomMenuShowTypeStyle}
        className="cycle-history-footer-bar"
      >
        <ToggleButtonCustom
          buttonProp={showTypeButtonProps}
          defaultValue={HISTORY_SHOW_TYPE.ALL}
          value={showType}
          onClick={OnShowTypeChangeActionHandler}
          disabled={false}
        />
      </div>
      <input
        id={HISTORY_SHOW_TYPE_DATE_OPTION.SEARCH_FILTER_BEGIN}
        css={CycleHistoryBottomMenuSearchFilterStyle}
        style={{marginRight: '8px'}}
        onClick={OnDateTypeOptionChangeActionHandler}
        value={searchFilterBegin.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })}
        readOnly
        disabled={disableDateAction}
      />
      <div css={dash}>{emptyContent}</div>
      <input
        id={HISTORY_SHOW_TYPE_DATE_OPTION.SEARCH_FILTER_END}
        css={CycleHistoryBottomMenuSearchFilterStyle}
        style={{marginLeft: '8px'}}
        onClick={OnDateTypeOptionChangeActionHandler}
        value={searchFilterEnd.toLocaleDateString('en-US', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        })}
        readOnly
        disabled={disableDateAction}
      />
      <DateTimePicker
        id={HISTORY_SHOW_TYPE_DATE_OPTION.SEARCH_FILTER_BEGIN}
        showDateTime={isDisplayBeginDateSelection}
        onClose={OnDateSelectionChangeActionHandler}
        dateFormat={DateTimePicker.format.date}
        timeFormat={false}
        isValidDate={limitCalendarDateRange}
        value={searchFilterBegin}
      />

      <DateTimePicker
        id={HISTORY_SHOW_TYPE_DATE_OPTION.SEARCH_FILTER_END}
        showDateTime={isDisplayEndDateSelection}
        onClose={OnDateSelectionChangeActionHandler}
        dateFormat={DateTimePicker.format.date}
        timeFormat={false}
        isValidDate={limitCalendarDateRange}
        value={searchFilterEnd}
      />
      <span css={SortbyText}>SORT BY</span>

      <div />
      <div
        className="cycle-history-footer-bar-sorting"
        ref={sortRef}
      >
        <FmlxDropdown
          id="dropdown-sorting-cycle-log"
          items={sortingItems}
          onChange={(e) => OnShowSortChangeActionHandler(e, sortingItems)}
          selectedItem={selectedItem}
          separator={false}
          menuProps={{
            anchorOrigin: {
              vertical: -(
                MENU_ITEM_DROPDOWN_HEIGHT * sortingItems.length +
                // eslint-disable-next-line no-unsafe-optional-chaining
                sortRef?.current?.getBoundingClientRect().height * 0.6
              ),
              horizontal: 'center',
            },
            sx: {
              '.MuiMenu-list': {
                maxHeight: 'unset',
                overflow: 'unset',
              },
            },
          }}
          // menuPosition={FmlxDropdown.MenuPosition.TOP}
        />
      </div>
    </>
  );
}

export default CycleHistoryBottomMenu;
